'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence, Variants } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useRouter, useSearchParams } from 'next/navigation'
import { signIn, getSession } from 'next-auth/react'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { usePartnerPortalStore } from '@/stores/partnerPortalStore'
import { siloApi } from '@/lib/api/silo-api'
import { GlassCard } from '../ui/GlassCard'
import {
  ShoppingCart,
  History,
  HeadphonesIcon,
  Mail,
  Phone,
  Building2,
  MapPin,
  User,
  Loader2,
  CheckCircle2,
  AlertCircle,
  LogIn,
  Sparkles,
  Key,
  MessageSquare
} from 'lucide-react'

// Form validation schema
const partnerRequestSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  businessName: z.string().min(2, 'Business name is required'),
  businessAddress: z.string().min(10, 'Please provide complete address'),
  acceptsTerms: z.boolean().refine(val => val === true, 'You must accept terms')
})

type PartnerRequestFormData = z.infer<typeof partnerRequestSchema>

// Login schema
const loginSchema = z.object({
  email: z.string().optional(),
  phone: z.string().optional(),
  password: z.string().optional(),
  loginMethod: z.enum(['code', 'password']).default('code')
})

type LoginFormData = z.infer<typeof loginSchema>

// DEBUG: Track button clicks globally
let buttonClickCounter = 0

export function PartnerPortalSection() {
  const [activeView, setActiveView] = useState<'benefits' | 'request' | 'login'>('login')
  const [isFormExpanded, setIsFormExpanded] = useState(true)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const router = useRouter()
  const searchParams = useSearchParams()
  const returnUrl = searchParams?.get('returnUrl') || searchParams?.get('callbackUrl') || undefined
  
  const {
    submitPartnerRequest,
    requestFormStatus,
    requestFormError,
    resetRequestForm,
    setRequestFormStatus
  } = usePartnerPortalStore()

  // Partner Request Form
  const requestForm = useForm<PartnerRequestFormData>({
    resolver: zodResolver(partnerRequestSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      businessName: '',
      businessAddress: '',
      acceptsTerms: false
    }
  })

  // Login Form - Default to code (not password)
  const [loginMethod, setLoginMethod] = useState<'code' | 'password'>('code')
  const [loginStep, setLoginStep] = useState<'email' | 'code' | 'pending'>('email')
  const [otpTransport, setOtpTransport] = useState<'sms' | 'email'>('sms') // Default to SMS/phone
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', ''])
  const [otpEmail, setOtpEmail] = useState('')
  const otpInputRefs = React.useRef<(HTMLInputElement | null)[]>([])

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema) as any,
    defaultValues: {
      email: '',
      phone: '',
      password: '',
      loginMethod: 'password'
    }
  })

  const handleRequestSubmit = async (data: PartnerRequestFormData) => {
    // Call API directly like Contact Us form - don't use store for submission
    setRequestFormStatus('submitting')
    try {
      // Parse address into components for backend
      const addressParts = data.businessAddress.split(',').map(part => part.trim())
      const street = addressParts[0] || ''
      const city = addressParts[addressParts.length - 3] || ''
      const stateZip = addressParts[addressParts.length - 2] || ''
      const [state, zipcode] = stateZip.split(' ').filter(Boolean)

      await siloApi.submitPublicContactForm({
        name: data.name,
        email: data.email,
        company: data.businessName,
        phone: data.phone,
        request: `Partner Portal Access Application`,
        subject: 'Partnership Portal Request',
        inquiry_type: 'partner_portal_request',
        company_address: street,
        company_city: city,
        company_state: state || '',
        company_zipcode: zipcode || '',
        metadata: {
          source: 'partner_portal_form',
          inquiry_type: 'partner_portal_request'
        }
      })

      // Set success status directly
      setRequestFormStatus('success')
    } catch (error) {
      setRequestFormStatus('error')
      throw error
    }
  }

  // Auto-open login form when redirected with callbackUrl
  React.useEffect(() => {
    if (returnUrl) {
      setActiveView('login')
      setIsFormExpanded(true)
    }
  }, [returnUrl])

  // Close/cleanup after success
  React.useEffect(() => {
    if (requestFormStatus === 'success') {
      const t = setTimeout(() => {
        setIsFormExpanded(false)
        requestForm.reset()
        resetRequestForm()
      }, 3000)
      return () => clearTimeout(t)
    }
  }, [requestFormStatus])

  const handleLoginSubmit = async (data: LoginFormData) => {
    const clickNumber = ++buttonClickCounter
    console.log(`🔵🔵🔵 [BUTTON CLICK #${clickNumber}] handleLoginSubmit called 🔵🔵🔵`)
    console.log(`🔵 [BUTTON CLICK #${clickNumber}] Total clicks so far: ${buttonClickCounter}`)

    if (loginMethod === 'code') {
      // OTP flow - Step 1: Request code
      if (loginStep === 'email') {
        try {
          console.log(`🔵 [FRONTEND #${clickNumber}] OTP request initiated at`, new Date().toISOString())
          setIsLoggingIn(true)
          setLoginError(null)

          // Get username based on transport type
          const username = otpTransport === 'sms' ? data.phone : data.email
          console.log(`🔵 [FRONTEND #${clickNumber}] Transport:`, otpTransport)
          console.log(`🔵 [FRONTEND #${clickNumber}] Username:`, username)

          if (!username) {
            console.log(`🔵 [FRONTEND #${clickNumber}] ❌ No username provided`)
            setLoginError(otpTransport === 'sms' ? 'Please enter your phone number' : 'Please enter your email')
            setIsLoggingIn(false)
            return
          }
          setOtpEmail(username) // Store for display and verification

          console.log(`🔵 [FRONTEND #${clickNumber}] Calling /api/otp/request with transport: ${otpTransport}...`)
          const response = await fetch('/api/otp/request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: username,
              transport: otpTransport  // Send transport preference to backend
            })
          })

          console.log(`🔵 [FRONTEND #${clickNumber}] Response status:`, response.status)
          console.log(`🔵 [FRONTEND #${clickNumber}] Response ok:`, response.ok)

          const responseData = await response.json()
          console.log(`🔵 [FRONTEND #${clickNumber}] Response data:`, JSON.stringify(responseData))

          // Use HTTP status (200) instead of checking response.success field
          if (response.ok) {
            console.log(`🔵 [FRONTEND #${clickNumber}] ✅ SUCCESS (200): Moving to code input step`)
            setLoginStep('code')
            setIsLoggingIn(false)  // Clear loading - code sent successfully, ready for user to enter code
            setTimeout(() => otpInputRefs.current[0]?.focus(), 100)
          } else {
            console.log(`🔵 [FRONTEND #${clickNumber}] ❌ ERROR (${response.status}): Showing error to user`)
            console.log(`🔵 [FRONTEND #${clickNumber}] Error message:`, responseData.error || 'Failed to send login code')
            setLoginError(responseData.error || 'Failed to send login code')
            setIsLoggingIn(false)  // Clear loading on error
          }
        } catch (error) {
          console.log(`🔵 [FRONTEND #${clickNumber}] 💥 EXCEPTION:`, error)
          setLoginError('An unexpected error occurred')
          setIsLoggingIn(false)  // Clear loading on error
        }
      }
    } else {
      // Password flow
      try {
        setIsLoggingIn(true)
        setLoginError(null)
        const result = await signIn('silo-password', {
          username: data.email,
          password: data.password,
          redirect: false,
        })

        if (result?.ok) {
          const session = await getSession()
          const user: any = (session as any)?.user

          // Priority order: Admin → Farm → Portal → Pending
          const isAdmin = user?.is_staff || user?.is_superuser
          const hasFarmAccess = user?.has_farm_access === true
          const hasPortalAccess = user?.portal_access === true

          console.log('[Password Login] User routing - Admin:', isAdmin, 'Farm:', hasFarmAccess, 'Portal:', hasPortalAccess)

          // 1. Admins → CRM Dashboard
          if (isAdmin) {
            console.log('[Password Login] → Admin: /silo/crm/dashboard')
            window.location.href = '/silo/crm/dashboard'
            return
          }

          // 2. Farm Users → Harvest Dashboard
          if (hasFarmAccess) {
            console.log('[Password Login] → Farm User: /silo/harvest')
            window.location.href = '/silo/harvest'
            return
          }

          // 3. Portal Users → Customer Portal
          if (hasPortalAccess) {
            console.log('[Password Login] → Portal User: /portal')
            window.location.href = returnUrl || '/portal'
            return
          }

          // 4. No access → Pending approval
          console.log('[Password Login] → No access: pending approval')
          setLoginStep('pending')
          setOtpEmail(user?.email || data.email || '')
          setIsLoggingIn(false)
          return
        } else {
          // Provide user-friendly error messages
          let errorMessage = "Invalid email or password"

          if (result?.error === "CredentialsSignin") {
            errorMessage = "Invalid email or password. Please check your credentials and try again."
          } else if (result?.error === "AccessDenied") {
            errorMessage = "Access denied. Your account may not have permission to access this resource."
          } else if (result?.error === "Configuration") {
            errorMessage = "There's a problem with the server configuration. Please try again later."
          } else if (result?.error) {
            // If there's a specific error message, use it
            errorMessage = result.error
          }

          setLoginError(errorMessage)
          setIsLoggingIn(false)  // Only clear on error
        }
      } catch (e) {
        setLoginError('Unable to sign in. Please try again.')
        setIsLoggingIn(false)  // Only clear on error
      }
      // No finally - keep loading state true during redirect
    }
  }

  // OTP Step 2: Verify code
  const handleVerifyOTP = async () => {
    const code = otpCode.join('')
    if (code.length !== 6) {
      setLoginError('Please enter all 6 digits')
      return
    }

    try {
      setIsLoggingIn(true)
      setLoginError(null)

      console.log('🔐 [OTP Verify] Starting verification with code:', code)
      console.log('🔐 [OTP Verify] Username (email/phone):', otpEmail)

      const result = await signIn('silo-otp', {
        username: otpEmail,
        code: code,
        redirect: false
      })

      console.log('🔐 [OTP Verify] signIn result:', result)
      console.log('🔐 [OTP Verify] result.ok:', result?.ok)
      console.log('🔐 [OTP Verify] result.error:', result?.error)

      console.log('🔐 [OTP Verify] ========================================')
      console.log('🔐 [OTP Verify] CHECKING SIGNIN RESULT')
      console.log('🔐 [OTP Verify] result object:', JSON.stringify(result, null, 2))
      console.log('🔐 [OTP Verify] result.ok:', result?.ok)
      console.log('🔐 [OTP Verify] result.error:', result?.error)
      console.log('🔐 [OTP Verify] result.status:', result?.status)
      console.log('🔐 [OTP Verify] result.url:', result?.url)

      if (result?.ok) {
        console.log('🔐 [OTP Verify] ✅ signIn returned OK - fetching session')

        // Wait for NextAuth to set the session properly
        let session = null
        let attempts = 0
        const maxAttempts = 10

        // Retry getSession until we have a valid session or timeout
        while (!session && attempts < maxAttempts) {
          attempts++
          console.log(`🔐 [OTP Verify] Attempt ${attempts}/${maxAttempts} to get session...`)

          session = await getSession()

          console.log(`🔐 [OTP Verify] Attempt ${attempts} - Session:`, session)
          console.log(`🔐 [OTP Verify] Attempt ${attempts} - Has user:`, !!session?.user)

          if (!session || !session.user) {
            console.log(`🔐 [OTP Verify] Attempt ${attempts} - Session not ready, waiting 500ms...`)
            await new Promise(resolve => setTimeout(resolve, 500))
          }
        }

        if (!session || !session.user) {
          console.error('🔐 [OTP Verify] ❌ CRITICAL: Session never populated after signin success!')
          console.error('🔐 [OTP Verify] This should not happen - NextAuth signIn succeeded but session is empty')
          setLoginError('Authentication succeeded but session failed to load. Please try again.')
          setLoginStep('email')
          return
        }

        console.log('🔐 [OTP Verify] ✅ Session loaded successfully')
        console.log('🔐 [OTP Verify] Full session object:', JSON.stringify(session, null, 2))

        const user: any = session.user
        console.log('🔐 [OTP Verify] User email:', user?.email)
        console.log('🔐 [OTP Verify] User id:', user?.id)
        console.log('🔐 [OTP Verify] User portal_access:', user?.portal_access)
        console.log('🔐 [OTP Verify] User has_farm_access:', user?.has_farm_access)
        console.log('🔐 [OTP Verify] User is_staff:', user?.is_staff)
        console.log('🔐 [OTP Verify] User is_superuser:', user?.is_superuser)

        // Priority order: Admin → Farm → Portal → Pending
        const isAdmin = user?.is_staff === true || user?.is_superuser === true
        const hasFarmAccess = user?.has_farm_access === true
        const hasPortalAccess = user?.portal_access === true

        console.log('🔐 [OTP Verify] User type - Admin:', isAdmin, 'Farm:', hasFarmAccess, 'Portal:', hasPortalAccess)

        // 1. Admins → CRM Dashboard
        if (isAdmin) {
          console.log('🔐 [OTP Verify] ✅ ADMIN USER - Redirecting to /silo/crm/dashboard')
          window.location.href = '/silo/crm/dashboard'
          return
        }

        // 2. Farm Users → Harvest Dashboard
        if (hasFarmAccess) {
          console.log('🔐 [OTP Verify] ✅ FARM USER - Redirecting to /silo/harvest')
          window.location.href = '/silo/harvest'
          return
        }

        // 3. Portal Users → Customer Portal
        if (hasPortalAccess) {
          console.log('🔐 [OTP Verify] ✅ PORTAL USER - Redirecting to /portal')
          window.location.href = returnUrl || '/portal'
          return
        }

        // 4. No access → Pending approval
        console.log('🔐 [OTP Verify] ❌ No access granted - showing pending approval screen')
        setLoginStep('pending')
        return
      } else {
        console.log('🔐 [OTP Verify] ❌ signIn returned NOT OK')
        console.log('🔐 [OTP Verify] Error from signIn:', result?.error)
        setLoginError(result?.error || 'Invalid code')
        setOtpCode(['', '', '', '', '', ''])
        otpInputRefs.current[0]?.focus()
        setIsLoggingIn(false)  // Only clear loading on error
      }
    } catch (error) {
      console.error('🔐 [OTP Verify] 💥 Exception caught:', error)
      setLoginError('Verification failed')
      setIsLoggingIn(false)  // Only clear loading on error
    }
    // Don't use finally - we want to keep loading state true during redirect
  }

  // OTP input handlers
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const newOtp = [...otpCode]
    newOtp[index] = value.slice(-1)
    setOtpCode(newOtp)
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    }
    if (e.key === 'Enter' && otpCode.every(d => d !== '')) {
      handleVerifyOTP()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '')
    if (pasteData.length === 6) {
      setOtpCode(pasteData.split(''))
      otpInputRefs.current[5]?.focus()
    }
  }

  // Animation variants
  const containerVariants: Variants = {
    collapsed: { 
      height: 0, 
      opacity: 0,
      transition: { type: 'spring', duration: 0.4 }
    },
    expanded: { 
      height: 'auto', 
      opacity: 1,
      transition: { 
        height: { type: 'spring', duration: 0.4 },
        opacity: { duration: 0.3, delay: 0.1 }
      }
    }
  }

  const fieldVariants = {
    hidden: { opacity: 0 },
    visible: (i: number) => ({
      opacity: 1,
      transition: { delay: 0.05 + i * 0.05, duration: 0.25 }
    })
  }

  const benefits = [
    {
      icon: <ShoppingCart className="h-5 w-5" />,
      title: 'Online Ordering',
      description: 'Access our full catalog and place orders 24/7',
      gradient: 'from-blue-500/20 via-blue-400/10'
    },
    {
      icon: <History className="h-5 w-5" />,
      title: 'Tracking & History',
      description: 'Track shipments and access complete order history',
      gradient: 'from-purple-500/20 via-purple-400/10'
    },
    {
      icon: <HeadphonesIcon className="h-5 w-5" />,
      title: 'Dedicated Support',
      description: 'Direct access to formulation and order support',
      gradient: 'from-yellow-500/20 via-yellow-400/10'
    }
  ]

  const processSteps = [
    {
      step: 1,
      title: 'Initial Consultation',
      description: 'We begin with a detailed consultation to understand your brand, products, and goals.'
    },
    {
      step: 2,
      title: 'Formulation & Development',
      description: 'Our team works with yours to develop custom formulations that meet your specifications.'
    },
    {
      step: 3,
      title: 'Testing & Refinement',
      description: 'We test and refine formulations to ensure they deliver the desired effects and quality.'
    },
    {
      step: 4,
      title: 'Production & Ongoing Support',
      description: 'We provide reliable production and continued support throughout your product lifecycle.'
    }
  ]

  return (
    <div className="w-full">
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        
        {/* Left Column: Benefits and Process */}
        <div className="space-y-6">
          {/* Benefits Section - Single row, 3 across on lg+ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="h-full"
              >
                <GlassCard className="p-4 h-full hover:shadow-elev-1 transition-all duration-300" disableInteractions>
                  {/* Title row: icon + heading */}
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${benefit.gradient} to-transparent backdrop-blur-sm border border-white/10`}>
                      {benefit.icon}
                    </div>
                    <h3 className="text-base font-semibold text-white">
                      {benefit.title}
                    </h3>
                  </div>
                  {/* Content row: full-width description */}
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">
                    {benefit.description}
                  </p>
                </GlassCard>
              </motion.div>
            ))}
          </div>

          {/* Partnership Process - Moved under benefits */}
          <GlassCard className="p-6 gradient-border">
            <h3 className="text-lg font-semibold mb-4">
              <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">Partnership</span>{' '}
              <span className="text-eskerium-gold">Process</span>
            </h3>
            <div className="space-y-4">
              {processSteps.map((process, index) => (
                <div key={index} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-eskerium-gold/30 to-eskerium-gold/20 border border-eskerium-gold/50 flex items-center justify-center text-eskerium-gold text-sm font-bold flex-shrink-0">
                    {process.step}
                  </div>
                  <div>
                    <h4 className="font-medium text-white text-sm mb-1">
                      {process.title}
                    </h4>
                    <p className="text-slate-400 text-xs leading-relaxed">
                      {process.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* Right Column: Action Buttons and Forms */}
        <div>
          <div className="sticky top-0">
            {/* Tab Headers - Outside Card */}
            <div className="flex items-center justify-center gap-8 mb-4">
              <button
                onClick={() => {
                  setActiveView('login')
                  setIsFormExpanded(true)
                }}
                className={`text-lg font-semibold transition-all duration-300 ${
                  activeView === 'login'
                    ? 'text-white drop-shadow-[0_0_15px_rgba(59,130,246,0.8)]'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                Login
              </button>
              <button
                onClick={() => {
                  setActiveView('request')
                  setIsFormExpanded(true)
                }}
                className={`text-lg font-semibold transition-all duration-300 ${
                  activeView === 'request'
                    ? 'text-white drop-shadow-[0_0_15px_rgba(234,179,8,0.8)]'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                Request Access
              </button>
            </div>

            <GlassCard className="p-4 sm:p-6 gradient-border overflow-visible" glowColor="blue" disableInteractions>

              {/* Animated Form Sections */}
              <AnimatePresence mode="wait">
                {/* Partner Request Form */}
                {activeView === 'request' && (
                  <motion.div
                    key="request-form"
                    variants={containerVariants}
                    initial="collapsed"
                    animate={isFormExpanded ? "expanded" : "collapsed"}
                    exit="collapsed"
                    style={{ overflow: 'hidden' }}
                  >
                    <div className="w-full">
                      {requestFormStatus === 'submitting' ? (
                        <div className="py-12 text-center">
                          <motion.div initial={{ rotate: 0 }} animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }} className="mx-auto mb-4 w-10 h-10 text-purple-400">
                            <Loader2 className="w-10 h-10" />
                          </motion.div>
                          <h3 className="text-xl font-semibold text-white mb-1">Submitting Application…</h3>
                          <p className="text-white/70 text-sm">Please wait while we send your details.</p>
                        </div>
                      ) : requestFormStatus === 'success' ? (
                        <div className="py-12 text-center">
                          <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
                          <h3 className="text-xl font-semibold text-white mb-1">Thank You!</h3>
                          <p className="text-white/70 text-sm">We received your application. We will be in touch shortly.</p>
                        </div>
                      ) : (
                      <Form {...requestForm}>
                        <form onSubmit={requestForm.handleSubmit(handleRequestSubmit)} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Name Field */}
                            <motion.div custom={0} variants={fieldVariants} initial="hidden" animate="visible">
                              <FormField
                                control={requestForm.control}
                                name="name"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                      <User className="h-3 w-3" />
                                      Your Name
                                    </FormLabel>
                                    <FormControl>
                                      <Input 
                                        {...field} 
                                        placeholder="John Doe"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 h-9 text-sm"
                                      />
                                    </FormControl>
                                    <FormMessage className="text-red-400 text-xs" />
                                  </FormItem>
                                )}
                              />
                            </motion.div>

                            {/* Email Field */}
                            <motion.div custom={1} variants={fieldVariants} initial="hidden" animate="visible">
                              <FormField
                                control={requestForm.control}
                                name="email"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                      <Mail className="h-3 w-3" />
                                      Email Address
                                    </FormLabel>
                                    <FormControl>
                                      <Input 
                                        {...field} 
                                        type="email"
                                        placeholder="john@company.com"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 h-9 text-sm"
                                      />
                                    </FormControl>
                                    <FormMessage className="text-red-400 text-xs" />
                                  </FormItem>
                                )}
                              />
                            </motion.div>

                            {/* Phone Field */}
                            <motion.div custom={2} variants={fieldVariants} initial="hidden" animate="visible">
                              <FormField
                                control={requestForm.control}
                                name="phone"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                      <Phone className="h-3 w-3" />
                                      Phone Number
                                    </FormLabel>
                                    <FormControl>
                                      <Input 
                                        {...field} 
                                        placeholder="(555) 123-4567"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 h-9 text-sm"
                                      />
                                    </FormControl>
                                    <FormMessage className="text-red-400 text-xs" />
                                  </FormItem>
                                )}
                              />
                            </motion.div>

                            {/* Business Name Field */}
                            <motion.div custom={3} variants={fieldVariants} initial="hidden" animate="visible">
                              <FormField
                                control={requestForm.control}
                                name="businessName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                      <Building2 className="h-3 w-3" />
                                      Business Name
                                    </FormLabel>
                                    <FormControl>
                                      <Input 
                                        {...field} 
                                        placeholder="Acme Corporation"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 h-9 text-sm"
                                      />
                                    </FormControl>
                                    <FormMessage className="text-red-400 text-xs" />
                                  </FormItem>
                                )}
                              />
                            </motion.div>
                          </div>

                          {/* Business Address Field */}
                          <motion.div custom={4} variants={fieldVariants} initial="hidden" animate="visible">
                            <FormField
                              control={requestForm.control}
                              name="businessAddress"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                    <MapPin className="h-3 w-3" />
                                    Business Address
                                  </FormLabel>
                                  <FormControl>
                                    <Textarea 
                                      {...field} 
                                      placeholder="123 Main St, Suite 100, City, State 12345"
                                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 min-h-[60px] resize-none text-sm"
                                    />
                                  </FormControl>
                                  <FormMessage className="text-red-400 text-xs" />
                                </FormItem>
                              )}
                            />
                          </motion.div>

                          {/* Terms Checkbox */}
                          <motion.div custom={5} variants={fieldVariants} initial="hidden" animate="visible">
                            <FormField
                              control={requestForm.control}
                              name="acceptsTerms"
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value}
                                      onCheckedChange={field.onChange}
                                      className="border-white/20 data-[state=checked]:bg-brand-primary data-[state=checked]:border-brand-primary"
                                    />
                                  </FormControl>
                                  <div className="space-y-1 leading-none">
                                    <FormLabel className="text-white/80 text-xs cursor-pointer">
                                      I agree to the{' '}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          // Trigger the same terms modal as footer
                                          const termsButton = document.querySelector('[data-terms-modal]') as HTMLElement
                                          termsButton?.click()
                                        }}
                                        className="text-blue-400 hover:text-blue-300 underline"
                                      >
                                        terms of service
                                      </button>
                                    </FormLabel>
                                    <FormMessage className="text-red-400 text-xs" />
                                  </div>
                                </FormItem>
                              )}
                            />
                          </motion.div>

                          {/* Submit Button */}
                          {requestFormStatus !== 'success' && (
                            <motion.div custom={6} variants={fieldVariants} initial="hidden" animate="visible">
                              <Button
                                type="submit"
                                disabled={requestFormStatus === 'submitting'}
                                className="w-full py-5 text-sm font-semibold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full transition-all duration-300"
                              >
                                {requestFormStatus === 'submitting' ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Submitting Request...
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="mr-2 h-4 w-4" />
                                    Submit Partnership Request
                                  </>
                                )}
                              </Button>
                            </motion.div>
                          )}
                        </form>
                      </Form>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Partner Login Form */}
                {activeView === 'login' && (
                  <motion.div
                    key="login-form"
                    variants={containerVariants}
                    initial="collapsed"
                    animate={isFormExpanded ? "expanded" : "collapsed"}
                    exit="collapsed"
                    style={{ overflow: 'visible' }}
                  >
                    <div className="w-full">
                      {/* Login Method Toggle - Better Mobile Design */}
                      <motion.div custom={0} variants={fieldVariants} initial="hidden" animate="visible" className="mb-4 px-2">
                        <div className="flex flex-row gap-2 justify-center">
                          <button
                            type="button"
                            onClick={() => {
                              setLoginMethod('code')
                              setLoginStep('email')
                              setOtpCode(['', '', '', '', '', ''])
                              setLoginError(null)
                            }}
                            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full flex-1 transition-all duration-200 backdrop-blur-md ${
                              loginMethod === 'code'
                                ? 'bg-blue-500/30 border-2 border-blue-400/80 text-white shadow-[0_0_25px_rgba(59,130,246,0.6),0_0_50px_rgba(59,130,246,0.3)]'
                                : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/80 hover:bg-white/10 hover:border-white/20'
                            }`}
                          >
                            <MessageSquare className="h-5 w-5" />
                            <span className="text-sm font-medium">Login Code</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setLoginMethod('password')
                              setLoginStep('email')
                              setLoginError(null)
                            }}
                            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full flex-1 transition-all duration-200 backdrop-blur-md ${
                              loginMethod === 'password'
                                ? 'bg-purple-500/30 border-2 border-purple-400/80 text-white shadow-[0_0_25px_rgba(168,85,247,0.6),0_0_50px_rgba(168,85,247,0.3)]'
                                : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/80 hover:bg-white/10 hover:border-white/20'
                            }`}
                          >
                            <Key className="h-5 w-5" />
                            <span className="text-sm font-medium">Password</span>
                          </button>
                        </div>
                      </motion.div>

                      {/* EMAIL INPUT VIEW (loginStep === 'email') */}
                      {loginStep === 'email' && (
                        <Form {...loginForm}>
                          <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="space-y-4">
                            {/* For Code method: Show Phone or Email based on transport toggle */}
                            {loginMethod === 'code' && (
                              <>
                                {/* OTP Transport Toggle */}
                                <motion.div custom={1} variants={fieldVariants} initial="hidden" animate="visible" className="px-2">
                                  <div className="flex gap-2 mb-4">
                                    <button
                                      type="button"
                                      onClick={() => setOtpTransport('sms')}
                                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all backdrop-blur-md ${
                                        otpTransport === 'sms'
                                          ? 'bg-green-500/30 border-2 border-green-400/80 text-white shadow-[0_0_20px_rgba(34,197,94,0.5),0_0_40px_rgba(34,197,94,0.25)]'
                                          : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/80 hover:bg-white/10 hover:border-white/20'
                                      }`}
                                    >
                                      <Phone className="h-4 w-4" />
                                      <span className="text-sm font-medium">Phone</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setOtpTransport('email')}
                                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all backdrop-blur-md ${
                                        otpTransport === 'email'
                                          ? 'bg-purple-500/30 border-2 border-purple-400/80 text-white shadow-[0_0_20px_rgba(168,85,247,0.5),0_0_40px_rgba(168,85,247,0.25)]'
                                          : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/80 hover:bg-white/10 hover:border-white/20'
                                      }`}
                                    >
                                      <Mail className="h-4 w-4" />
                                      <span className="text-sm font-medium">Email</span>
                                    </button>
                                  </div>
                                </motion.div>

                                {/* Info message based on transport */}
                                <motion.div custom={2} variants={fieldVariants} initial="hidden" animate="visible">
                                  <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                    <p className="text-sm text-white/80">
                                      {otpTransport === 'sms'
                                        ? "Please enter the phone number on your account, and we'll send you an access code via SMS."
                                        : "Please enter the email on your account, and we'll send you an access code via email."}
                                    </p>
                                  </div>
                                </motion.div>

                                {/* Phone Input (when SMS selected) */}
                                {otpTransport === 'sms' && (
                                  <motion.div custom={3} variants={fieldVariants} initial="hidden" animate="visible">
                                    <FormField
                                      control={loginForm.control}
                                      name="phone"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                            <Phone className="h-3 w-3" />
                                            Phone Number
                                          </FormLabel>
                                          <FormControl>
                                            <Input
                                              {...field}
                                              type="tel"
                                              placeholder="(555) 123-4567"
                                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 h-9 text-sm"
                                            />
                                          </FormControl>
                                          <FormMessage className="text-red-400 text-xs" />
                                        </FormItem>
                                      )}
                                    />
                                  </motion.div>
                                )}

                                {/* Email Input (when Email selected) */}
                                {otpTransport === 'email' && (
                                  <motion.div custom={3} variants={fieldVariants} initial="hidden" animate="visible">
                                    <FormField
                                      control={loginForm.control}
                                      name="email"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                            <Mail className="h-3 w-3" />
                                            Email Address
                                          </FormLabel>
                                          <FormControl>
                                            <Input
                                              {...field}
                                              type="email"
                                              placeholder="partner@company.com"
                                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 h-9 text-sm"
                                            />
                                          </FormControl>
                                          <FormMessage className="text-red-400 text-xs" />
                                        </FormItem>
                                      )}
                                    />
                                  </motion.div>
                                )}
                              </>
                            )}

                            {/* For Password method: Show email only */}
                            {loginMethod === 'password' && (
                              <motion.div custom={1} variants={fieldVariants} initial="hidden" animate="visible">
                                <FormField
                                  control={loginForm.control}
                                  name="email"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                        <Mail className="h-3 w-3" />
                                        Partner Email
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          {...field}
                                          type="email"
                                          placeholder="partner@company.com"
                                          className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 h-9 text-sm"
                                        />
                                      </FormControl>
                                      <FormMessage className="text-red-400 text-xs" />
                                    </FormItem>
                                  )}
                                />
                              </motion.div>
                            )}

                            {/* Password field - only shown when password method selected */}
                            {loginMethod === 'password' && (
                              <motion.div
                                custom={2}
                                variants={fieldVariants}
                                initial="hidden"
                                animate="visible"
                              >
                                <FormField
                                  control={loginForm.control}
                                  name="password"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="text-white/80 text-sm flex items-center gap-2">
                                        <LogIn className="h-3 w-3" />
                                        Password
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          {...field}
                                          type="password"
                                          placeholder="Enter your password"
                                          className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/20 focus:bg-white/10 h-9 text-sm"
                                        />
                                      </FormControl>
                                      <FormMessage className="text-red-400 text-xs" />
                                    </FormItem>
                                  )}
                                />
                              </motion.div>
                            )}

                            {loginError && (
                              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-red-400">
                                {loginError}
                              </motion.p>
                            )}

                            <motion.div custom={3} variants={fieldVariants} initial="hidden" animate="visible">
                              <Button
                                type="submit"
                                className="w-full py-5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isLoggingIn}
                              >
                                {loginMethod === 'code' ? (
                                  <>
                                    {isLoggingIn ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Sending Code...
                                      </>
                                    ) : (
                                      <>
                                        <Mail className="mr-2 h-4 w-4" />
                                        Send Login Code
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {isLoggingIn ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Signing In...
                                      </>
                                    ) : (
                                      <>
                                        <LogIn className="mr-2 h-4 w-4" />
                                        Sign In
                                      </>
                                    )}
                                  </>
                                )}
                              </Button>
                            </motion.div>
                          </form>
                        </Form>
                      )}

                      {/* OTP CODE INPUT VIEW (loginStep === 'code') */}
                      {loginStep === 'code' && (
                        <div className="space-y-4">
                          <div className="text-center mb-4">
                            <p className="text-sm text-white/70">
                              Enter the 6-digit code sent via {otpTransport === 'sms' ? 'SMS' : 'email'} to
                            </p>
                            <p className="text-sm font-semibold text-white">{otpEmail}</p>
                          </div>

                          <div className="flex justify-center gap-2">
                            {otpCode.map((digit, index) => (
                              <Input
                                key={index}
                                ref={el => { otpInputRefs.current[index] = el }}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                onPaste={index === 0 ? handleOtpPaste : undefined}
                                disabled={isLoggingIn}
                                className="w-12 h-14 text-center text-2xl font-bold bg-white/5 border-white/20 text-white focus:border-eskerium-blue/50 focus:ring-eskerium-blue/20"
                              />
                            ))}
                          </div>

                          {loginError && (
                            <p className="text-xs text-red-400 text-center">{loginError}</p>
                          )}

                          <Button
                            onClick={handleVerifyOTP}
                            className="w-full py-5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isLoggingIn || otpCode.some(d => d === '')}
                          >
                            {isLoggingIn ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Verifying Code...
                              </>
                            ) : (
                              'Verify Code'
                            )}
                          </Button>

                          <Button
                            variant="ghost"
                            onClick={() => {
                              setLoginStep('email')
                              setOtpCode(['', '', '', '', '', ''])
                              setLoginError(null)
                            }}
                            className="w-full text-white/70 hover:text-white hover:bg-white/10"
                            disabled={isLoggingIn}
                          >
                            Change Email
                          </Button>
                        </div>
                      )}

                      {/* PENDING APPROVAL VIEW (loginStep === 'pending') */}
                      {loginStep === 'pending' && (
                        <div className="space-y-4 text-center">
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                            <AlertCircle className="h-8 w-8 text-eskerium-gold mx-auto mb-2" />
                            <h3 className="text-lg font-semibold text-white mb-1">Account Pending Approval</h3>
                            <p className="text-sm text-white/70">
                              Thank you for signing in with <strong>{otpEmail}</strong>. Your account is currently pending approval.
                            </p>
                          </div>

                          <p className="text-sm text-white/60">
                            You'll receive an email notification when your account has been activated.
                          </p>

                          <p className="text-xs text-white/50">
                            Questions? Contact{' '}
                            <a href="mailto:support@eskerium.com" className="text-eskerium-blue hover:underline">
                              support@eskerium.com
                            </a>
                          </p>

                          <Button
                            variant="outline"
                            onClick={() => {
                              setLoginStep('email')
                              setOtpEmail('')
                              setOtpCode(['', '', '', '', '', ''])
                              setLoginError(null)
                              loginForm.reset()
                            }}
                            className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20"
                          >
                            Try Different Email
                          </Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </GlassCard>
          </div>
        </div>
      </div>
    </div>
  )
}